import { AttackPattern, PatternState } from '../AttackPattern.js';

const PREVIEW_BEAT = 0.55; // dim aim-line shown at the next strike angle
const HOT_BEAT = 0.35; // beam is live and ticking at that angle
const SNAP_STEP = Math.PI / 6; // 30° — every strike angle sits on the conductor's grid

/**
 * Conductor's Baton (The Iron Cadence) — a beam that never sweeps. It
 * conducts: each measure it shows the next stroke as a dim aim line
 * (snapped to a 30° grid, stepped at most one grid notch toward the
 * player's current bearing), holds the rest, then SNAPS live along exactly
 * that line for one hot beat. A sweeping laser is dodged by outrunning it;
 * the baton is dodged by reading — the stroke never lands anywhere it
 * didn't just show you, but it also never stops herding toward you.
 */
export class ConductorsBaton extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: "Conductor's Baton", telegraphDuration: 1.0, activeDuration: 4.5, minPhase });
    this.config = config;
  }

  onBegin(boss, player) {
    // Open on the grid angle nearest the player, so stroke one is honest.
    this._angle = this._snap(Math.atan2(player.y - boss.y, player.x - boss.x));
    this._clock = 0;
    this._hot = false;
    this._tickTimer = 0;
  }

  _snap(angle) {
    return Math.round(angle / SNAP_STEP) * SNAP_STEP;
  }

  /** Steps the stroke one grid notch toward the player's current bearing — quantized pursuit. */
  _restep(boss, player) {
    const desired = Math.atan2(player.y - boss.y, player.x - boss.x);
    let diff = this._snap(desired) - this._angle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    if (diff !== 0) this._angle += Math.sign(diff) * SNAP_STEP;
  }

  onActiveTick(dt, boss, player, _pool) {
    const measure = PREVIEW_BEAT + HOT_BEAT;
    this._clock += dt;
    if (this._clock >= measure) {
      this._clock -= measure;
      this._hot = false;
      this._restep(boss, player);
    }

    const wasHot = this._hot;
    this._hot = this._clock >= PREVIEW_BEAT;
    if (this._hot && !wasHot) {
      boss.eventBus?.emit('cadence:beat');
      this._tickTimer = 0; // first damage check lands immediately on the snap
    }
    if (!this._hot) return;

    this._tickTimer -= dt;
    if (this._tickTimer > 0) return;
    this._tickTimer = this.config.tickInterval;
    if (player.isInvincible) return;

    // Perpendicular distance to the beam ray (forward half only).
    const dx = player.x - boss.x;
    const dy = player.y - boss.y;
    const along = dx * Math.cos(this._angle) + dy * Math.sin(this._angle);
    if (along < 0) return;
    const across = Math.abs(-dx * Math.sin(this._angle) + dy * Math.cos(this._angle));
    if (across < this.config.width / 2 + player.radius) {
      player.takeDamage(this.config.tickDamage);
    }
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'laserAim', x: boss.x, y: boss.y, angle: this._angle, length: 9999, color: '#9fb8e8' };
    }
    if (this.state !== PatternState.ACTIVE) return null;
    return this._hot
      ? { kind: 'laserBeam', x: boss.x, y: boss.y, angle: this._angle, length: 9999, width: this.config.width, color: '#9fb8e8' }
      : { kind: 'laserAim', x: boss.x, y: boss.y, angle: this._angle, length: 9999, color: '#9fb8e8' };
  }
}
